/**
 * Contains methods to transform report rows data and generate rows
 * as per specified OPPU Hierarchy. The generated data structure is flattened
 * i.e. an array of rows with parent/child information in each rows to
 * render a hierarchical display as required.
 */

import { deepClone } from '../../../utilities';
import { EXPANSION_STATUS } from './toggle';
import { calculateAggregateRows } from './row_aggregations';
import { CHANNELS } from '../../../constants/channels';

const OPPU_HIERARCHY_LEVELS = Object.freeze({
  SUMMARY: 'summary',
  CHANNEL: 'channel',
});

const OPPU_HIERARCHY_CONFIGS = Object.freeze({
  '0_GMS': { level: OPPU_HIERARCHY_LEVELS.SUMMARY, component: 'GMS' },
  '1_Units': { level: OPPU_HIERARCHY_LEVELS.SUMMARY, component: 'Units' },
  '2_RETAIL': { level: OPPU_HIERARCHY_LEVELS.CHANNEL, channel: 'Retail' },
  '3_FBA': { level: OPPU_HIERARCHY_LEVELS.CHANNEL, channel: 'FBA' },
  '4_MFN': { level: OPPU_HIERARCHY_LEVELS.CHANNEL, channel: 'MFN' },
  '5_ALL': { level: OPPU_HIERARCHY_LEVELS.CHANNEL, channel: 'All' },
});

export const OPPU_GROUPING_ITEMS = ['Vendor Profit', 'Gross Profit'];
const OPPU_SPECIAL_GROUPING_ITEMS = ['Contribution Profit'];

export const ROW_TYPES = Object.freeze({
  SECTION_HEAD: 'sectionHead',
  SUB_SECTION_HEAD: 'subSectionHead',
  FIRST_CHILD: 'firstChild',
  MIDDLE_CHILD: 'middleChild',
  LAST_CHILD: 'lastChild',
});

export const PLACEMENT = Object.freeze({
  TOP: 'top',
  BOTTOM: 'bottom',
});

export const getRowsWithOppuHierarchy = (reportRows, oppuHierachy, requiredChannels = ['all']) => {
  return oppuHierachy
    .map((h) =>
      getRowsForOppuHierarchyItem(
        reportRows,
        h,
        requiredChannels.map((c) => c.toLowerCase())
      )
    )
    .flat();
};

const getRowsForOppuHierarchyItem = (reportRows, oppuHierachyItem, requiredChannels) => {
  let resultRows = [];
  const { id, mapping } = oppuHierachyItem;
  const config = OPPU_HIERARCHY_CONFIGS[id];

  const allChannels = requiredChannels.includes('all');
  if (!allChannels && config.channel && !requiredChannels.includes(config.channel.toLowerCase())) {
    return resultRows;
  }

  const { component, level, channel } = config;

  if (level === OPPU_HIERARCHY_LEVELS.SUMMARY) {
    resultRows.push(...computeSummaryRows(reportRows, component, mapping, allChannels));
  }

  if (level === OPPU_HIERARCHY_LEVELS.CHANNEL) {
    resultRows.push(...computeChannelRows(reportRows, channel, mapping));
  }

  return resultRows;
};

/**
 * Compute rows for summary components, i.e GMS and UNITS
 */
const computeSummaryRows = (reportRows, component, mapping, allChannels) => {
  const channels = mapping.mapping.map((m) => m.name);

  const childRows = channels
    .map((c, i) => {
      const rows = findRows(reportRows, component, c);
      return rows.map((r) => {
        const rowType = getChildrenRowType(i, channels);

        return {
          ...r,
          level: 1,
          visible: true,
          loaded: true,
          rowMetaInfo: { type: rowType, parentPlacement: PLACEMENT.TOP },
        };
      });
    })
    .flat();

  const parentRow = allChannels
    ? {
        component: mapping.name,
        channel: CHANNELS.ALL,
        status: EXPANSION_STATUS.COLLAPSE,
        visible: true,
        level: 0,
        children: childRows.map((r) => `${r.channel}/${r.component}`),
        ...calculateAggregateRows(childRows),
        rowMetaInfo: { type: ROW_TYPES.SECTION_HEAD, childrenPlacement: PLACEMENT.BOTTOM },
      }
    : {};

  return [parentRow, ...childRows];
};

/**
 * Compute rows for channels - i.e. ALL, RETAIL, FBA, MFN.
 */
const computeChannelRows = (reportRows, channel, mapping) => {
  let resultRows = [];

  resultRows.push({
    component: mapping.name,
    visible: true,
    level: 0,
    children: mapping.mapping.map((m) => `${channel}/${m.name}`),
    status: EXPANSION_STATUS.COLLAPSE,
  });

  let configuredMappings = deepClone(mapping.mapping);

  while (configuredMappings.length > 0) {
    const mappingItem = configuredMappings.shift();
    if (mappingItem.mapping) {
      const { item, children } = getMappingItemWithChildren(mappingItem, channel);

      if (mappingItem.style === PLACEMENT.TOP) {
        configuredMappings.unshift(item);
        configuredMappings.unshift(...children);
      } else {
        configuredMappings.unshift(...children);
        configuredMappings.unshift(item);
      }
    } else {
      const component = mappingItem.name;
      const { level, children, rowMetaInfo } = mappingItem;

      if (OPPU_GROUPING_ITEMS.includes(component)) {
        resultRows.push({
          component,
          channel,
          level,
          visible: true,
          children,
          status: EXPANSION_STATUS.COLLAPSE,
          loaded: true,
          rowMetaInfo: { type: ROW_TYPES.SUB_SECTION_HEAD, childrenPlacement: PLACEMENT.TOP },
        });
      } else {
        let rows = findRows(reportRows, component, channel);
        let visible = true;
        let loaded = true;

        if (rows && !rows.length) {
          rows = [{ component, channel }];
          visible = false;
          loaded = false;
        }

        resultRows.push(
          ...rows.map((r) => {
            return {
              ...r,
              level,
              visible,
              loaded,
              children,
              status:
                !children || OPPU_SPECIAL_GROUPING_ITEMS.includes(component)
                  ? EXPANSION_STATUS.COLLAPSE
                  : EXPANSION_STATUS.EXPAND,
              rowMetaInfo: OPPU_SPECIAL_GROUPING_ITEMS.includes(component)
                ? { type: ROW_TYPES.SUB_SECTION_HEAD, childrenPlacement: PLACEMENT.TOP }
                : rowMetaInfo,
            };
          })
        );
      }
    }
  }

  return resultRows;
};

const getMappingItemWithChildren = (mappingItem, channel) => {
  const level = mappingItem.level || 1;
  const parentPlacement = mappingItem.style === PLACEMENT.TOP ? PLACEMENT.BOTTOM : PLACEMENT.TOP;
  const childrenPlacement = mappingItem.style === PLACEMENT.TOP ? PLACEMENT.TOP : PLACEMENT.BOTTOM;

  const children = [
    ...mappingItem.mapping.map((m, i) => {
      const rowType = getChildrenRowType(i, mappingItem.mapping);
      return { ...m, level: level + 1, rowMetaInfo: { type: rowType, parentPlacement } };
    }),
  ];
  const item = {
    ...mappingItem,
    mapping: null,
    level,
    children: children.map((c) => `${channel}/${c.name}`),
    rowMetaInfo: { childrenPlacement },
  };

  return { item, children };
};

const getChildrenRowType = (i, allChildrenItems) => {
  return i === 0
    ? ROW_TYPES.FIRST_CHILD
    : i === allChildrenItems.length - 1
    ? ROW_TYPES.LAST_CHILD
    : ROW_TYPES.MIDDLE_CHILD;
};

export const findRows = (reportRows, component, channel) => {
  return reportRows.filter(
    (r) =>
      (r.component || '').toLowerCase() === (component || '').toLowerCase() &&
      (r.channel || '').toLowerCase() === (channel || '').toLowerCase()
  );
};

export const findRowIndex = (reportRows, component, channel) => {
  return reportRows.findIndex(
    (r) =>
      (r.component || '').toLowerCase() === (component || '').toLowerCase() &&
      (r.channel || '').toLowerCase() === (channel || '').toLowerCase()
  );
};

export const findChildRowIndex = (child, reportRows) => {
  const parts = child.split('/');
  const childChannel = parts[0];
  const childComponent = parts[1];
  return findRowIndex(reportRows, childComponent, childChannel);
};



// WEBPACK FOOTER //
// ./src/components/reports/utils/oppu_hierarchy.js